import { useRef, useMemo, useState } from "react";
import type { FuriganaSegment } from "@/types/subtitle";
import { cn } from "@/lib/utils";

// --- Types ---

type WordSegment = { text: string; start: number; end: number };
type TokenSegment = { text: string; start: number; end: number; isWordLike: boolean };

// --- Helper Functions ---

export function segmentJapaneseWords(text: string): WordSegment[] {
  if (!text) return [];

  const SegmenterCtor = (Intl as unknown as { Segmenter?: any }).Segmenter;
  if (typeof SegmenterCtor === "function") {
    try {
      const segmenter = new SegmenterCtor("ja", { granularity: "word" });
      const out: WordSegment[] = [];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      for (const item of segmenter.segment(text)) {
        const seg = item as {
          segment: string;
          index: number;
          isWordLike?: boolean;
        };

        if (seg.isWordLike) {
          out.push({
            text: seg.segment,
            start: seg.index,
            end: seg.index + seg.segment.length,
          });
        }
      }

      return out.length ? out : [{ text, start: 0, end: text.length }];
    } catch {
      return [{ text, start: 0, end: text.length }];
    }
  }

  return [{ text, start: 0, end: text.length }];
}

export function segmentJapaneseTokens(text: string): TokenSegment[] {
  if (!text) return [];

  const SegmenterCtor = (Intl as unknown as { Segmenter?: any }).Segmenter;
  if (typeof SegmenterCtor === "function") {
    try {
      const segmenter = new SegmenterCtor("ja", { granularity: "word" });
      const out: TokenSegment[] = [];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      for (const item of segmenter.segment(text)) {
        const seg = item as {
          segment: string;
          index: number;
          isWordLike?: boolean;
        };

        out.push({
          text: seg.segment,
          start: seg.index,
          end: seg.index + seg.segment.length,
          isWordLike: !!seg.isWordLike,
        });
      }

      return out.length
        ? out
        : [{ text, start: 0, end: text.length, isWordLike: true }];
    } catch {
      return [{ text, start: 0, end: text.length, isWordLike: true }];
    }
  }

  return [{ text, start: 0, end: text.length, isWordLike: true }];
}

export function isDelimiterToken(t: string): boolean {
  return /[、。！？!?；;：:，,・…]/.test(t);
}

export function normalizeLookupText(raw: string) {
  const trimmed = raw.trim();
  const cleaned = trimmed
    .replace(/^[\s\u3000、。！？!?「」『』（）()【】\[\]《》〈〉・,，.]+/, "")
    .replace(/[\s\u3000、。！？!?「」『』（）()【】\[\]《》〈〉・,，.]+$/, "");
  return cleaned || trimmed;
}

function katakanaToHiragana(value: string) {
  return value.replace(/[\u30a1-\u30f6]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x60));
}

function shouldRenderRubyReading(text: string, reading?: string) {
  if (!reading) return false;
  return /[\u4e00-\u9faf]/.test(text);
}

export function findWordAtIndex(words: WordSegment[], index: number) {
  return words.find((w) => index >= w.start && index < w.end)?.text;
}

/**
 * Merge furigana data with original Japanese text.
 * AI-generated furigana may only contain kanji readings, not the full sentence.
 * This function reconstructs the full text with furigana annotations.
 */
export function mergeFuriganaWithText(japanese: string, furiganaData: FuriganaSegment[]): FuriganaSegment[] {
  if (!japanese || !furiganaData?.length) {
    return [{ text: japanese || "" }];
  }

  // Build a map of word -> reading from furigana data
  const readingMap = new Map<string, string>();
  for (const seg of furiganaData) {
    if (seg.text && seg.reading) {
      readingMap.set(seg.text, seg.reading);
    }
  }

  // If furigana already covers the full text, return as-is
  const furiganaText = furiganaData.map(s => s.text).join("");
  if (furiganaText === japanese) {
    return furiganaData;
  }

  // Otherwise, segment the original text and apply readings where available
  const tokens = segmentJapaneseTokens(japanese);
  const result: FuriganaSegment[] = [];

  for (const token of tokens) {
    const reading = readingMap.get(token.text);
    if (reading) {
      result.push({ text: token.text, reading });
    } else {
      result.push({ text: token.text });
    }
  }

  return result;
}

export const posColorMap: Record<string, string> = {
  "名詞": "underline decoration-sky-500/60 dark:decoration-sky-400/80 decoration-2 underline-offset-4",
  "動詞": "underline decoration-green-500/60 dark:decoration-green-400/80 decoration-2 underline-offset-4",
  "形容詞": "underline decoration-amber-500/60 dark:decoration-amber-400/80 decoration-2 underline-offset-4",
  "副詞": "underline decoration-purple-500/60 dark:decoration-purple-400/80 decoration-2 underline-offset-4",
  "助詞": "underline decoration-rose-500/60 dark:decoration-rose-400/80 decoration-2 underline-offset-4",
  "助動詞": "underline decoration-blue-500/60 dark:decoration-blue-400/80 decoration-2 underline-offset-4",
  "連体詞": "underline decoration-orange-500/60 dark:decoration-orange-400/80 decoration-2 underline-offset-4",
  "接続詞": "underline decoration-gray-500/60 dark:decoration-gray-400/80 decoration-2 underline-offset-4",
};

export function FuriganaText({ 
  segments,
  japanese,
  onWordClick,
  onWordHover,
  showFurigana = true,
  active = false,
  progress = 0,
  hideUnderline = false,
  hideGrammarBackground = false,
  hideShadowingSpotlight = false,
  shadowingMode = false,
  highlightNewWords = true,
  vocabularySet = new Set(),
  tokenizer,
  enablePosTagging = false,
  fontSize = 1,
  blurStrength = 0,
}: { 
  segments: FuriganaSegment[];
  japanese: string;
  onWordClick: (word: string, rect: DOMRect) => void;
  onWordHover: (word: string, rect: DOMRect) => void;
  showFurigana?: boolean;
  active?: boolean;
  progress?: number;
  hideUnderline?: boolean;
  hideGrammarBackground?: boolean;
  hideShadowingSpotlight?: boolean;
  shadowingMode?: boolean;
  highlightNewWords?: boolean;
  vocabularySet?: Set<string>;
  tokenizer?: any; // Add tokenizer prop
  enablePosTagging?: boolean;
  fontSize?: number;
  blurStrength?: number;
}) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const longPressRef = useRef<number | null>(null);
  const longPressTriggered = useRef(false);
  const pressStartRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const LONG_PRESS_CANCEL_MOVE_PX = 10;
  const linePalette = useMemo(
    () => [
      { bg: "bg-amber-100/50 dark:bg-amber-900/20", underline: "bg-amber-300" },
      { bg: "bg-sky-100/50 dark:bg-sky-900/20", underline: "bg-sky-300" },
      { bg: "bg-emerald-100/50 dark:bg-emerald-900/20", underline: "bg-emerald-300" },
      { bg: "bg-rose-100/50 dark:bg-rose-900/20", underline: "bg-rose-300" },
    ],
    []
  );

  // Merge furigana data with original text to ensure complete rendering
  const mergedSegments = useMemo(
    () => mergeFuriganaWithText(japanese, segments),
    [japanese, segments]
  );

  // Attach POS data using Kuromoji
  const segmentsWithPos = useMemo(() => {
    if (!tokenizer || !enablePosTagging || !mergedSegments.length) return mergedSegments;

    const tokens = tokenizer.tokenize(japanese);
    let currentIndex = 0;
    
    return mergedSegments.map(seg => {
      // Find token that covers the start of this segment
      const token = tokens.find((t: any) => 
        t.word_position <= currentIndex && 
        (t.word_position + t.surface_form.length) > currentIndex
      );
      
      const pos = token ? token.pos : undefined;
      const newSeg = { ...seg, pos };
      currentIndex += seg.text.length;
      return newSeg;
    });
  }, [enablePosTagging, japanese, mergedSegments, tokenizer]);

  const fullText = useMemo(() => segmentsWithPos.map((s) => s.text).join(""), [segmentsWithPos]);
  const segmentStarts = useMemo(() => {
    let offset = 0;
    return segmentsWithPos.map((seg) => {
      const start = offset;
      offset += seg.text.length;
      return start;
    });
  }, [segmentsWithPos]);
  const wordSegments = useMemo(() => segmentJapaneseWords(fullText), [fullText]);
  const phraseGroups = useMemo(() => {
    const groups: number[][] = [];
    let current: number[] = [];
    for (let i = 0; i < segmentsWithPos.length; i++) {
      const t = segmentsWithPos[i]?.text || "";
      if (isDelimiterToken(t)) {
        if (current.length) {
          groups.push(current);
          current = [];
        }
        continue;
      }
      current.push(i);
    }
    if (current.length) groups.push(current);
    return groups;
  }, [segmentsWithPos]);

  const tokenOrder = useMemo(
    () => segmentsWithPos.map((_, idx) => idx).filter((i) => {
      const t = segmentsWithPos[i]?.text || "";
      return !isDelimiterToken(t) && t.trim().length > 0;
    }),
    [segmentsWithPos]
  );
  const activeTokenIndex = useMemo(() => {
    if (!active || tokenOrder.length === 0) return -1;
    const idx = Math.floor(Math.max(0, Math.min(0.999, progress)) * tokenOrder.length);
    return Math.max(0, Math.min(idx, tokenOrder.length - 1));
  }, [active, tokenOrder, progress]);

  const clearLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  // Handle press start
  const handlePointerDown = (e: React.PointerEvent, segIndex: number) => {
    longPressTriggered.current = false;
    clearLongPress();
    pressStartRef.current = { pointerId: e.pointerId, x: e.clientX, y: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const start = pressStartRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.hypot(dx, dy) > LONG_PRESS_CANCEL_MOVE_PX) {
      clearLongPress();
    }
  };

  const handlePointerUp = (e: React.PointerEvent, segIndex: number) => {
    const start = pressStartRef.current;
    clearLongPress();
    pressStartRef.current = null;
    if (!start || start.pointerId !== e.pointerId) return;
    
    // Check if total movement is small enough to be a tap
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.hypot(dx, dy) > LONG_PRESS_CANCEL_MOVE_PX) return;

    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const charIndex = segmentStarts[segIndex] ?? 0;
    const word = normalizeLookupText(
      findWordAtIndex(wordSegments, charIndex) ?? segmentsWithPos[segIndex]?.text ?? ""
    );
    if (word) onWordClick(word, rect);
  };

  const handlePointerCancel = () => {
    clearLongPress();
    longPressTriggered.current = false;
    pressStartRef.current = null;
  };

  // Handle text selection for phrases/words (desktop)
  const handleMouseUp = (e: React.MouseEvent) => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();
    
    if (selectedText && selectedText.length > 0) {
      e.stopPropagation();
      const range = selection?.getRangeAt(0);
      if (range) {
        const rect = range.getBoundingClientRect();
        const normalized = normalizeLookupText(selectedText);
        if (normalized) onWordClick(normalized, rect);
      }
    }
  };

  return (
    <span 
      ref={containerRef} 
      data-dict="1"
      className="leading-snug max-[390px]:leading-normal sm:leading-loose select-none md:select-text transition-all duration-300 block"
      style={{ 
        fontSize: `${fontSize}em`,
        filter: blurStrength > 0 ? `blur(${blurStrength}px)` : 'none'
      }}
      onMouseEnter={(e) => {
        if (blurStrength > 0) e.currentTarget.style.filter = 'none';
      }}
      onMouseLeave={(e) => {
        if (blurStrength > 0) e.currentTarget.style.filter = `blur(${blurStrength}px)`;
      }}
      onMouseUp={handleMouseUp}
    >
      {phraseGroups.map((group, pIdx) => (
        <span
          key={`phrase-${pIdx}`}
          className={cn(
            "inline whitespace-normal mr-1 max-[390px]:mr-0.5 align-baseline",
            // active && !hideGrammarBackground ? linePalette[pIdx % linePalette.length].bg : "" 
          )}
        >
          {group.map((i) => {
            const seg = segmentsWithPos[i];
            const hasText = !!seg?.text && seg.text.trim().length > 0;
            const hasReading = !!seg?.reading;
            
            // Check if word is in vocabulary
            const charIndex = segmentStarts[i] ?? 0;
            const word = normalizeLookupText(
              findWordAtIndex(wordSegments, charIndex) ?? seg?.text ?? ""
            );
            const isSaved = word && vocabularySet.has(word);
            const textColor = highlightNewWords && isSaved ? "text-red-500 font-medium" : "";
            const blurClass = shadowingMode ? "blur-sm hover:blur-none transition-all duration-300" : "";
            
            // POS Coloring
            const posClass = enablePosTagging && !hideUnderline && (seg as any).pos && posColorMap[(seg as any).pos] 
              ? posColorMap[(seg as any).pos]
              : "";

            return hasReading && showFurigana ? (
          <ruby 
            key={i} 
            data-dict="1"
            className={cn(
              "group/word relative cursor-pointer [ruby-position:over] px-0.5 max-[390px]:px-0 mx-0 transition-all hover:outline hover:outline-1 hover:outline-foreground/20",
              // First item in phrase: rounded-l
              i === group[0] ? "rounded-l-md pl-1" : "",
              // Last item in phrase (if no delimiter): rounded-r
              i === group[group.length - 1] ? "rounded-r-md pr-1" : "",
              // Middle item: no border radius
              
              !hideGrammarBackground ? linePalette[pIdx % linePalette.length].bg : "",
              textColor,
              blurClass,
              posClass
            )}
            onPointerDown={(e) => handlePointerDown(e, i)}
            onPointerMove={handlePointerMove}
            onPointerUp={(e) => handlePointerUp(e, i)}
            onPointerCancel={handlePointerCancel}
            onPointerLeave={handlePointerCancel}
            onMouseEnter={(e) => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              if (word) onWordHover(word, rect);
            }}
          >
            {seg?.text}
            <rp>(</rp>
            <rt className="text-[9px] sm:text-[11px] md:text-[12px] font-normal leading-none select-none">
              {seg?.reading ? katakanaToHiragana(String(seg.reading)) : ""}
            </rt>
            <rp>)</rp>
              {!hideUnderline && (!enablePosTagging || !(seg as any).pos) && (
                <span
                  className={cn(
                    "absolute left-0 right-0 -bottom-0.5 h-1 rounded opacity-70 group-hover/word:opacity-100",
                    active ? linePalette[pIdx % linePalette.length].underline : "bg-muted",
                    !hideShadowingSpotlight && tokenOrder[activeTokenIndex] === i && active ? "opacity-100 ring-2 ring-emerald-500" : ""
                  )}
                />
              )}
              {!hideUnderline && enablePosTagging && (seg as any).pos && !hideShadowingSpotlight && tokenOrder[activeTokenIndex] === i && active && (
                 <span className="absolute inset-0 ring-2 ring-emerald-500 rounded pointer-events-none" />
              )}
          </ruby>
        ) : (
          <ruby 
            key={i} 
            data-dict={hasText ? "1" : undefined}
            className={hasText ? cn(
              "group/word relative cursor-pointer [ruby-position:over] px-0.5 max-[390px]:px-0 mx-0 transition-all hover:outline hover:outline-1 hover:outline-foreground/20",
              // First item in phrase
              i === group[0] ? "rounded-l-md pl-1" : "",
              // Last item in phrase
              i === group[group.length - 1] ? "rounded-r-md pr-1" : "",

              !hideGrammarBackground ? linePalette[pIdx % linePalette.length].bg : "",
              textColor,
              blurClass,
              posClass
            ) : ""}
            onPointerDown={(e) => hasText && handlePointerDown(e, i)}
            onPointerMove={handlePointerMove}
            onPointerUp={(e) => handlePointerUp(e, i)}
            onPointerCancel={handlePointerCancel}
            onPointerLeave={handlePointerCancel}
            onMouseEnter={(e) => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              if (word) onWordHover(word, rect);
            }}
          >
            {seg?.text}
            {/* Invisible RT to maintain height alignment if showFurigana is true */}
            {showFurigana && <rt className="text-[9px] sm:text-[11px] md:text-[12px] font-normal leading-none select-none opacity-0">&nbsp;</rt>}
              {!hideUnderline && (!enablePosTagging || !(seg as any).pos) && (
                <span
                  className={cn(
                    "absolute left-0 right-0 -bottom-0.5 h-1 rounded opacity-70 group-hover/word:opacity-100",
                    active ? linePalette[pIdx % linePalette.length].underline : "bg-muted",
                    !hideShadowingSpotlight && tokenOrder[activeTokenIndex] === i && active ? "opacity-100 ring-2 ring-emerald-500" : ""
                  )}
                />
              )}
              {!hideUnderline && enablePosTagging && hasText && (seg as any).pos && !hideShadowingSpotlight && tokenOrder[activeTokenIndex] === i && active && (
                 <span className="absolute inset-0 ring-2 ring-emerald-500 rounded pointer-events-none" />
              )}
          </ruby>
        );
          })}
          {/* Render delimiter that follows this phrase, plain text without box */}
          {(() => {
            const phraseEndIndex = group[group.length - 1];
            const nextIndex = phraseEndIndex + 1;
            const delimText = segmentsWithPos[nextIndex]?.text || "";
            if (segmentsWithPos[nextIndex] && isDelimiterToken(delimText)) {
              return (
                <span className="ml-0.5">
                  {delimText}
                </span>
              );
            }
            return null;
          })()}
        </span>
      ))}
    </span>
  );
}

export function SegmentedJapaneseText({
  text,
  onWordClick,
  onWordHover,
  active = false,
  progress = 0,
  showFurigana = true,
  hideUnderline = false,
  hideGrammarBackground = false,
  hideShadowingSpotlight = false,
  shadowingMode = false,
  showOnlyChinese = false,
  highlightNewWords = true,
  vocabularySet = new Set(),
  tokenizer,
  enablePosTagging = false,
  fontSize = 1,
  blurStrength = 0,
}: {
  text: string;
  onWordClick: (word: string, rect: DOMRect) => void;
  onWordHover: (word: string, rect: DOMRect) => void;
  active?: boolean;
  progress?: number;
  showFurigana?: boolean;
  hideUnderline?: boolean;
  hideGrammarBackground?: boolean;
  hideShadowingSpotlight?: boolean;
  shadowingMode?: boolean;
  showOnlyChinese?: boolean;
  highlightNewWords?: boolean;
  vocabularySet?: Set<string>;
  tokenizer?: any; // Add tokenizer prop
  enablePosTagging?: boolean;
  fontSize?: number;
  blurStrength?: number;
}) {
  const mergedSegments = useMemo(() => {
    if (showOnlyChinese) return [];
    
    if (tokenizer) {
      const tokens = tokenizer.tokenize(text);
      return tokens.map((t: any) => {
        const start = Math.max(0, Number(t.word_position || 1) - 1);
        return {
          text: t.surface_form,
          // Kuromoji word_position is 1-based in most builds.
          start,
          end: start + t.surface_form.length,
          isWordLike: !/記号|symbol|punctuation/i.test(String(t.pos || "")),
          pos: enablePosTagging ? t.pos : undefined,
          reading: t.reading ? katakanaToHiragana(String(t.reading)) : undefined,
        };
      });
    }

    return segmentJapaneseTokens(text);
  }, [enablePosTagging, text, showOnlyChinese, tokenizer]);

  const wordSegments = useMemo(() => segmentJapaneseWords(text), [text]);

  const longPressRef = useRef<number | null>(null);
  const longPressTriggered = useRef(false);
  const pressStartRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const LONG_PRESS_CANCEL_MOVE_PX = 10;
  const linePalette = useMemo(
    () => [
      { bg: "bg-amber-100/50 dark:bg-amber-900/20", underline: "bg-amber-300" },
      { bg: "bg-sky-100/50 dark:bg-sky-900/20", underline: "bg-sky-300" },
      { bg: "bg-emerald-100/50 dark:bg-emerald-900/20", underline: "bg-emerald-300" },
      { bg: "bg-rose-100/50 dark:bg-rose-900/20", underline: "bg-rose-300" },
    ],
    []
  );

  const clearLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  const handlePointerDown = (e: React.PointerEvent, tokenText: string) => {
    longPressTriggered.current = false;
    clearLongPress();
    pressStartRef.current = { pointerId: e.pointerId, x: e.clientX, y: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const start = pressStartRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.hypot(dx, dy) > LONG_PRESS_CANCEL_MOVE_PX) {
      clearLongPress();
    }
  };

  const handlePointerUp = (e: React.PointerEvent, tokenText: string) => {
    const start = pressStartRef.current;
    clearLongPress();
    pressStartRef.current = null;
    if (!start || start.pointerId !== e.pointerId) return;

    // Check if total movement is small enough to be a tap
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.hypot(dx, dy) > LONG_PRESS_CANCEL_MOVE_PX) return;

    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const normalized = normalizeLookupText(tokenText);
    if (normalized) onWordClick(normalized, rect);
  };

  const handlePointerCancel = () => {
    clearLongPress();
    longPressTriggered.current = false;
    pressStartRef.current = null;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();
    if (selectedText && selectedText.length > 0) {
      e.stopPropagation();
      const range = selection?.getRangeAt(0);
      if (range) {
        const rect = range.getBoundingClientRect();
        const normalized = normalizeLookupText(selectedText);
        if (normalized) onWordClick(normalized, rect);
      }
    }
  };

  const tokenOrder = useMemo(
    () => mergedSegments.filter((t) => !isDelimiterToken(t.text) && t.text.trim().length),
    [mergedSegments]
  );
  const activeTokenIndex = useMemo(() => {
    if (!active || tokenOrder.length === 0) return -1;
    const idx = Math.floor(Math.max(0, Math.min(0.999, progress)) * tokenOrder.length);
    return Math.max(0, Math.min(idx, tokenOrder.length - 1));
  }, [active, tokenOrder, progress]);

  const phraseGroups = useMemo(() => {
    const groups: TokenSegment[][] = [];
    let current: TokenSegment[] = [];
    for (const t of mergedSegments) {
      if (isDelimiterToken(t.text)) {
        if (current.length) {
          groups.push(current);
          current = [];
        }
        continue;
      }
      current.push(t);
    }
    if (current.length) groups.push(current);
    return groups;
  }, [mergedSegments]);

  if (showOnlyChinese) return null;

  return (
    <span
      data-dict="1"
      className="leading-snug max-[390px]:leading-normal sm:leading-loose select-none md:select-text transition-all duration-300 block"
      style={{ 
        fontSize: `${fontSize}em`,
        filter: blurStrength > 0 ? `blur(${blurStrength}px)` : 'none'
      }}
      onMouseEnter={(e) => {
        if (blurStrength > 0) e.currentTarget.style.filter = 'none';
      }}
      onMouseLeave={(e) => {
        if (blurStrength > 0) e.currentTarget.style.filter = `blur(${blurStrength}px)`;
      }}
      onMouseUp={handleMouseUp}
    >
      {phraseGroups.map((group, pIdx) => (
        <span
          key={`phrase-${pIdx}`}
          className={cn(
            "inline whitespace-normal mr-1 max-[390px]:mr-0.5 align-baseline",
            // active && !hideGrammarBackground ? linePalette[pIdx % linePalette.length].bg : "" 
          )}
        >
          {group.map((t, idx) => {
            const hasText = !!t.text && t.text.trim().length > 0;
            // Determine text color (red for saved words)
            const word = findWordAtIndex(wordSegments, t.start);
            const isSaved = word && vocabularySet.has(normalizeLookupText(word));
            const textColor = highlightNewWords && isSaved ? "text-red-500 font-medium" : "";
            const blurClass = shadowingMode ? "blur-sm hover:blur-none transition-all duration-300" : "";
            
            // POS Coloring
            const posClass = enablePosTagging && !hideUnderline && (t as any).pos && posColorMap[(t as any).pos] 
              ? posColorMap[(t as any).pos]
              : "";

            return (
              <ruby
                key={`${t.start}-${idx}`}
                data-dict="1"
                className={cn(
                  "group/word relative cursor-pointer [ruby-position:over] px-0.5 max-[390px]:px-0 mx-0 transition-all hover:outline hover:outline-1 hover:outline-foreground/20",
                  // First item in phrase
                  idx === 0 ? "rounded-l-md pl-1" : "",
                  // Last item in phrase
                  idx === group.length - 1 ? "rounded-r-md pr-1" : "",

                  !hideGrammarBackground ? linePalette[pIdx % linePalette.length].bg : "",
                  textColor,
                  blurClass,
                  posClass
                )}
                onPointerDown={(e) => handlePointerDown(e, t.text)}
                onPointerMove={handlePointerMove}
                onPointerUp={(e) => handlePointerUp(e, t.text)}
                onPointerCancel={handlePointerCancel}
                onPointerLeave={handlePointerCancel}
                onMouseEnter={(e) => {
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const normalized = normalizeLookupText(t.text);
                  if (normalized) onWordHover(normalized, rect);
                }}
              >
                {t.text}
                {showFurigana && shouldRenderRubyReading(t.text, (t as any).reading) ? (
                  <rt className="text-[9px] sm:text-[11px] md:text-[12px] font-normal leading-none select-none">
                    {(t as any).reading}
                  </rt>
                ) : (
                  showFurigana && <rt className="text-[9px] sm:text-[11px] md:text-[12px] font-normal leading-none select-none opacity-0">&nbsp;</rt>
                )}
                {!hideUnderline && (!enablePosTagging || !(t as any).pos) && hasText && (
                  <span
                    className={cn(
                      "absolute left-0 right-0 -bottom-0.5 h-1 rounded opacity-70 group-hover/word:opacity-100",
                      active ? linePalette[pIdx % linePalette.length].underline : "bg-muted",
                      !hideShadowingSpotlight && tokenOrder[activeTokenIndex]?.start === t.start && active ? "opacity-100 ring-2 ring-emerald-500" : ""
                    )}
                  />
                )}
                {!hideUnderline && enablePosTagging && hasText && (t as any).pos && !hideShadowingSpotlight && tokenOrder[activeTokenIndex]?.start === t.start && active && (
                   <span className="absolute inset-0 ring-2 ring-emerald-500 rounded pointer-events-none" />
                )}
              </ruby>
            );
          })}
          {/* Delimiter after phrase */}
          {(() => {
            const nextToken = mergedSegments.find((tk) => tk.start === (group[group.length - 1]?.end));
            if (nextToken && isDelimiterToken(nextToken.text)) {
              return (
                <span className="ml-0.5">
                  {nextToken.text}
                </span>
              );
            }
            return null;
          })()}
        </span>
      ))}
    </span>
  );
}
